import { Alert, Button, Divider, Stack, Typography } from "@mui/material";
import { omit } from "lodash";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { SwitchElement, TextFieldElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { GqlPermissions, GqlUserUpdateInput, MeGetUserInfoDocument } from "~/__generated__";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import { UserContextUserData } from "~/modules/auth/context/UserContext";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";
import AddressAutocompleteField from "~/modules/map/components/AddressAutocompleteField";

import useFieldworkerUpdate from "../../api/useFieldworkerUpdate";
import useUserUpdate from "../../api/useUserUpdate";
import PhoneNumberField from "../PhoneNumberField";
import ProfilePhotoUploader from "../ProfilePhotoUploader";

type Props = {
    user: UserContextUserData;
};

type UserProfileUpdateFormData = Omit<GqlUserUpdateInput, "userId"> & {
    fieldworker: { addressId: string; hasOwnTransport: boolean };
};

export default function UserProfileUpdateForm({ user }: Props) {
    const { t } = useTranslation("user");
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);

    const updateUser = useUserUpdate(user.id, [MeGetUserInfoDocument]);
    const updateFieldworker = useFieldworkerUpdate(user.id, [MeGetUserInfoDocument]);

    const formMethods = useForm<UserProfileUpdateFormData>({
        values: {
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            phone: user.phone,
            roleId: user.tenantRole?.id || "",
            fieldworker: {
                addressId: "",
                hasOwnTransport: user.fieldworker?.hasOwnTransport || false,
            },
        },
    });

    const {
        resetField,
        control,
        handleSubmit,
        setError,
        getFieldState,
        formState: { errors, isLoading, isDirty, isSubmitted, isSubmitting },
    } = formMethods;

    const onSubmit: SubmitHandler<UserProfileUpdateFormData> = async data => {
        const { isDirty: isPhoneDirty } = getFieldState("phone");

        const fieldworkerData =
            user.fieldworker?.address && data.fieldworker.addressId !== ""
                ? data.fieldworker
                : omit(data.fieldworker, "addressId");

        const userPayload = isPhoneDirty ? omit(data, "fieldworker") : omit(data, "fieldworker", "phone");

        await Promise.all([
            updateUser.updateUser(userPayload),
            user.fieldworker ? updateFieldworker.fieldworkerUpdate(fieldworkerData) : Promise.resolve(),
        ]);

        // we call reset manually because addressId does not come back from the server and thus stays dirty
        resetField("fieldworker.addressId");
    };

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);
    useApplyErrorToForm(updateUser.error, setError);

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <GaiaConfirmCloseDialog blocker={blocker} />
                <Stack gap={2}>
                    {errors.root && (
                        <Alert severity="error">
                            <Typography>{errors.root.message}</Typography>
                        </Alert>
                    )}

                    <ProfilePhotoUploader user={user} />

                    <TextFieldElement name="firstName" label={t("userForm.labelFirstname")} required />
                    <TextFieldElement name="lastName" label={t("userForm.labelLastname")} required />

                    <Divider>
                        <Typography variant="caption" textTransform="uppercase">
                            {t("userForm.sectionContact")}
                        </Typography>
                    </Divider>

                    <TextFieldElement name="email" label={t("userForm.labelEmail")} required />

                    <PhoneNumberField<UserProfileUpdateFormData> control={control} name="phone" required />

                    {user.fieldworker && (
                        <>
                            <Divider>
                                <Typography variant="caption" textTransform="uppercase">
                                    {t("userForm.sectionFieldworkerDetails")}
                                </Typography>
                            </Divider>

                            <Controller
                                name="fieldworker.addressId"
                                control={formMethods.control}
                                render={({ field }) => (
                                    <AddressAutocompleteField
                                        fieldLabel={t("userForm.labelAddress")}
                                        noOptionsText={t("userForm.labelAddressNoOptions")}
                                        onSelect={value => field.onChange(value)}
                                        initialValue={user.fieldworker?.address}
                                        {...field}
                                        disabled={!isAdmin}
                                    />
                                )}
                            />

                            <SwitchElement name="fieldworker.hasOwnTransport" label={t("userForm.labelHaveACar")} />
                        </>
                    )}

                    <Button
                        sx={{ mt: 3 }}
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={!isDirty}
                        loading={isLoading}
                    >
                        {t("userForm.btnSubmit")}
                    </Button>

                    <Divider />
                </Stack>
            </form>
        </FormProvider>
    );
}
